/**
 * Design tokens and shared styles for Booking Details page (matches BookingDetailPage.jsx reference).
 */
export const bookingTokens = {
  bg: '#f2f6f3',
  white: '#ffffff',
  ink: '#0c1810',
  ink2: '#1e3328',
  subtle: '#4d6b58',
  muted: '#8ba898',
  border: '#dae6df',
  borderL: '#eaf2ec',
  green: '#0a6b3e',
  greenMd: '#0d8a50',
  greenLt: '#e4f5ec',
  greenSf: 'rgba(10,107,62,0.07)',
  amber: '#d97706',
  amberLt: '#fef3c7',
  amberBdr: '#fcd34d',
  red: '#dc2626',
  redLt: '#fee2e2',
  redBdr: '#fca5a5',
  blue: '#1d4ed8',
  blueLt: '#dbeafe',
  topBarBg: '#0c1810',
};

export const STATUS_CFG = {
  awaiting_approval: { label: 'Awaiting Approval', bg: '#fff7ed', color: '#c2410c', border: '#fed7aa', dot: '#f97316' },
  pending: { label: 'Pending', bg: bookingTokens.blueLt, color: bookingTokens.blue, border: '#93c5fd', dot: '#3b82f6' },
  draft: { label: 'Draft', bg: '#f8fafc', color: '#64748b', border: '#e2e8f0', dot: '#94a3b8' },
  cancelled: { label: 'Cancelled', bg: bookingTokens.redLt, color: bookingTokens.red, border: bookingTokens.redBdr, dot: '#ef4444' },
  active: { label: 'Active', bg: bookingTokens.blueLt, color: bookingTokens.blue, border: '#93c5fd', dot: '#3b82f6' },
  confirmed: { label: 'Confirmed', bg: bookingTokens.greenLt, color: bookingTokens.green, border: '#a7f3d0', dot: '#22c55e' },
  reserved: { label: 'Reserved', bg: '#f5f3ff', color: '#6d28d9', border: '#ddd6fe', dot: '#8b5cf6' },
  completed: { label: 'Completed', bg: bookingTokens.greenLt, color: bookingTokens.green, border: '#a7f3d0', dot: '#22c55e' },
  expired: { label: 'Expired', bg: '#f8fafc', color: '#64748b', border: '#e2e8f0', dot: '#94a3b8' },
};

/** MUI sx for card matching reference SCard */
export const cardSx = {
  background: bookingTokens.white,
  borderRadius: 2,
  border: '1.5px solid',
  borderColor: bookingTokens.border,
  boxShadow: '0 1px 6px rgba(10,40,20,0.05)',
  overflow: 'hidden',
};

/** MUI sx for card header (SCardHead) */
export const cardHeadSx = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '13px 18px',
  borderBottom: `1px solid ${bookingTokens.borderL}`,
};

/** MUI sx for stat box (neutral) */
export const statBoxSx = {
  flex: '1 1 110px',
  minWidth: 100,
  background: bookingTokens.bg,
  border: '1.5px solid',
  borderColor: bookingTokens.border,
  borderRadius: 1.5,
  padding: '13px 16px',
};

/** MUI sx for stat box with green accent */
export const statBoxAccentSx = {
  ...statBoxSx,
  background: 'linear-gradient(135deg,#0a6b3e,#0d8a50)',
  borderColor: 'transparent',
  boxShadow: '0 4px 18px rgba(10,107,62,0.22)',
  '& .statLabel': { color: 'rgba(255,255,255,0.6)' },
  '& .statValue': { color: '#fff' },
};

/** Table header row */
export const tableHeadSx = {
  '& .MuiTableRow-root': { background: '#f8fbf9' },
  '& th': {
    padding: '10px 16px',
    fontSize: 10,
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.55px',
    color: bookingTokens.subtle,
    borderBottom: `1.5px solid ${bookingTokens.border}`,
  },
};

/** Empty state container */
export const emptyStateSx = {
  textAlign: 'center',
  padding: '52px 20px',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 1.25,
};

/** Primary green button (GreenBtn) */
export const primaryButtonSx = {
  background: 'linear-gradient(135deg,#0a6b3e,#0d8a50)',
  color: '#fff',
  fontWeight: 700,
  textTransform: 'none',
  boxShadow: '0 2px 10px rgba(10,107,62,0.25)',
  '&:hover': {
    background: 'linear-gradient(135deg,#0d8a50,#0a6b3e)',
    boxShadow: '0 4px 14px rgba(10,107,62,0.35)',
  },
};

/** Ghost/secondary button */
export const ghostButtonSx = {
  border: '1.5px solid',
  borderColor: bookingTokens.border,
  color: bookingTokens.subtle,
  fontWeight: 600,
  textTransform: 'none',
  '&:hover': {
    borderColor: bookingTokens.subtle,
    bgcolor: bookingTokens.greenSf,
  },
};

export default bookingTokens;
